import React, { useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { editableContent } from "@/content/editableContent";
import { Heart, Lock, ShieldCheck, Info } from "lucide-react";

const PRESETS = editableContent.donation.presets.map((preset) => ({
  amount: preset.amount,
  impact_da: preset.impact.da,
  impact_en: preset.impact.en,
  impact_es: preset.impact.es,
}));

export default function Donate() {
  const { t, lang } = useI18n();
  const [amount, setAmount] = useState(500);
  const [custom, setCustom] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [dedication, setDedication] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const final = custom ? Number(custom) : amount;
  const preset = PRESETS.find(p => p.amount === final);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/donations/checkout", {
        amount: final, currency: "dkk", dedication, anonymous,
        email, name, origin_url: window.location.origin, language: lang,
      });
      window.location.href = data.checkout_url;
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Der opstod en fejl.");
      setBusy(false);
    }
  };

  return (
    <div className="max-editorial container-x py-16 grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-7">
        <div className="label-mono text-terracotta">{t("donate")}</div>
        <h1 className="font-editorial text-5xl md:text-6xl mt-3 tracking-tighter">{editableContent.donation.copy.headline[lang] || editableContent.donation.copy.headline.en}</h1>
        <p className="mt-4 text-ink/70 max-w-xl leading-relaxed">{editableContent.donation.copy.sub[lang] || editableContent.donation.copy.sub.en}</p>

        <form onSubmit={submit} className="mt-12 space-y-8" data-testid="donate-form">
          <div>
            <label className="label-mono text-ink/60">{t("amount_label")}</label>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
              {PRESETS.map(p => (
                <button key={p.amount} type="button" data-testid={`donate-preset-${p.amount}`}
                        onClick={() => { setAmount(p.amount); setCustom(""); }}
                        className={`p-4 border transition-colors text-left ${final === p.amount && !custom ? "border-terracotta bg-terracotta/5" : "border-line hover:border-ink"}`}>
                  <div className="font-editorial text-2xl">{p.amount} kr</div>
                  <div className="text-xs text-ink/60 mt-1">{p[`impact_${lang}`]}</div>
                </button>
              ))}
            </div>
            <input data-testid="donate-custom" type="number" min="10" value={custom} onChange={e => setCustom(e.target.value)}
                   placeholder={t("custom_amount")}
                   className="mt-3 w-full border border-line focus:border-terracotta outline-none px-4 py-3 bg-transparent" />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <input data-testid="donate-name" placeholder="Dit navn (valgfrit)" value={name} onChange={e => setName(e.target.value)}
                   className="w-full border border-line focus:border-terracotta outline-none px-4 py-3 bg-transparent" />
            <input data-testid="donate-email" type="email" required placeholder="E-mail (kvittering)" value={email} onChange={e => setEmail(e.target.value)}
                   className="w-full border border-line focus:border-terracotta outline-none px-4 py-3 bg-transparent" />
          </div>

          <input data-testid="donate-dedication" value={dedication} onChange={e => setDedication(e.target.value)}
                 placeholder='"In memory of…" (valgfrit)'
                 className="w-full border border-line focus:border-terracotta outline-none px-4 py-3 bg-transparent" />

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="donate-anonymous" checked={anonymous} onChange={e => setAnonymous(e.target.checked)} />
            Doner anonymt
          </label>

          <div className="grid md:grid-cols-2 gap-3">
            <button type="submit" disabled={busy || !final || final < 10} data-testid="donate-submit"
                    className="bg-terracotta hover:bg-terracotta-dark disabled:opacity-60 text-white px-6 py-4 text-sm tracking-wide inline-flex items-center justify-center gap-2">
              <Heart size={16} /> {busy ? "…" : `${t("proceed")} — ${final || 0} kr`}
            </button>
            <button type="button" disabled
                    data-testid="donate-mobilepay"
                    className="border border-line text-ink/50 px-6 py-4 text-sm tracking-wide cursor-not-allowed">
              MobilePay — {lang === "da" ? "kommer snart" : lang === "es" ? "próximamente" : "coming soon"}
            </button>
          </div>

          <div className="text-xs text-ink/40" data-testid="monthly-soon">{t("monthly_soon")}</div>

          {err && <div className="text-sm text-red-600" data-testid="donate-error">{err}</div>}
        </form>
      </div>

      <aside className="lg:col-span-4 lg:col-start-9">
        <div className="border border-line p-6 space-y-4">
          {preset && (
            <div>
              <div className="label-mono text-terracotta">Din indvirkning</div>
              <div className="mt-2 font-editorial text-2xl">{preset[`impact_${lang}`]}</div>
            </div>
          )}
          <div className="hairline pt-4">
            <div className="label-mono text-ink/50 mb-2">Trust</div>
            <ul className="text-sm space-y-2">
              <li className="flex items-center gap-2"><Lock size={14} /> {editableContent.donation.trustItems[0].label[lang] || editableContent.donation.trustItems[0].label.en}</li>
              <li className="flex items-center gap-2"><ShieldCheck size={14} /> {editableContent.donation.trustItems[1].label[lang] || editableContent.donation.trustItems[1].label.en} · CVR {editableContent.donation.cvr}</li>
              <li className="flex items-center gap-2"><ShieldCheck size={14} /> {t("trust_audit")}</li>
            </ul>
          </div>
        </div>
      </aside>
    </div>
  );
}
